import {
	type CanvasImageAsset,
	CanvasImageEffectName,
	type CanvasImageScene,
	type CanvasImageSource,
	type CanvasImageTransferCacheParams,
	type CanvasImageTransferData,
	type SceneGeometry
} from '@/types/canvas-image-effect';
import {
	createCanvasImageAsset,
	createCanvasImageController,
	createCanvasImageTransferData
} from '../utils/canvasImageEffect';

type CanvasImageRenderer = ReturnType<typeof createCanvasImageController>;

class CanvasImageEffectService {
	private static readonly TRANSFER_SETTLED_IMAGE_START = 0.84;

	private animationFrameId: number | null = null;
	private asset: CanvasImageAsset | null = null;
	private canvas: HTMLCanvasElement | null = null;
	private currentSourceKey: string | null = null;
	private renderer: CanvasImageRenderer | null = null;
	private scene: CanvasImageScene | null = null;
	private transferData: CanvasImageTransferData | null = null;
	private transferSignature: string | null = null;

	setScene(scene: CanvasImageScene) {
		this.scene = scene;
		const host = this.getSceneHost(scene);
		const canvas = this.ensureCanvas();

		if (canvas.parentElement !== host) {
			host.appendChild(canvas);
		}
	}

	async play(effect: CanvasImageEffectName, durationMs: number) {
		const scene = this.requireScene();
		const asset = await this.ensureSceneAsset(scene.image);
		const geometry = this.getSceneGeometry(scene);
		const renderer = this.getRenderer();
		const transferData =
			effect === CanvasImageEffectName.Transfer
				? this.getOrCreateTransferData({
						asset,
						finalCenterX: geometry.finalCenterX,
						imageHeight: geometry.initialHeight,
						imageWidth: geometry.initialWidth,
						initialCenterX: geometry.initialCenterX,
						initialCenterY: geometry.initialCenterY
					})
				: null;

		if (effect === CanvasImageEffectName.Transfer && !transferData) {
			return;
		}

		this.stopAnimation();
		if (effect === CanvasImageEffectName.Reveal) {
			this.animate(durationMs, (progress) => {
				renderer.revealImage({
					asset,
					centerX: geometry.initialCenterX,
					centerY: geometry.initialCenterY,
					imageHeight: geometry.initialHeight,
					imageWidth: geometry.initialWidth,
					progress,
					stageHeight: geometry.stageHeight,
					stageWidth: geometry.stageWidth
				});
			});
			return;
		}

		const preparedTransferData = transferData as CanvasImageTransferData;
		this.animate(durationMs, (progress) => {
			renderer.transferImageToRight({
				centerY: geometry.initialCenterY,
				imageHeight: geometry.initialHeight,
				imageWidth: geometry.initialWidth,
				progress,
				settledImageOpacity: this.smoothstep(
					CanvasImageEffectService.TRANSFER_SETTLED_IMAGE_START,
					1,
					progress
				),
				stageHeight: geometry.stageHeight,
				stageWidth: geometry.stageWidth,
				startCenterX: geometry.initialCenterX,
				transferData: preparedTransferData
			});
		});
	}

	reset() {
		this.stopAnimation();
		const context = this.canvas?.getContext('2d');
		if (context && this.canvas) {
			context.clearRect(0, 0, this.canvas.width, this.canvas.height);
		}
	}

	private animate(durationMs: number, renderFrame: (progress: number) => void) {
		if (durationMs <= 0) {
			renderFrame(1);
			return;
		}

		const startAt = performance.now();

		const tick = (now: number) => {
			const progress = this.clamp((now - startAt) / durationMs);
			renderFrame(progress);

			if (progress < 1) {
				this.animationFrameId = window.requestAnimationFrame(tick);
				return;
			}

			this.animationFrameId = null;
		};

		renderFrame(0);
		this.animationFrameId = window.requestAnimationFrame(tick);
	}

	private clamp(value: number, min = 0, max = 1) {
		return Math.min(max, Math.max(min, value));
	}

	private createSourceKey(source: CanvasImageSource) {
		return typeof source === 'string'
			? source
			: source.currentSrc || source.src;
	}

	private ensureCanvas() {
		if (!this.canvas) {
			this.canvas = document.createElement('canvas');
			this.canvas.setAttribute('aria-hidden', 'true');
			this.canvas.className =
				'pointer-events-none absolute inset-0 h-full w-full';
		}

		return this.canvas;
	}

	private async ensureSceneAsset(source: CanvasImageSource) {
		const sourceKey = this.createSourceKey(source);

		if (this.asset && this.currentSourceKey === sourceKey) {
			return this.asset;
		}

		const image = await this.loadSource(source);
		this.asset = createCanvasImageAsset(image);
		this.currentSourceKey = sourceKey;
		this.transferData = null;
		this.transferSignature = null;

		return this.asset;
	}

	private getRenderer() {
		if (!this.renderer) {
			this.renderer = createCanvasImageController(this.ensureCanvas());
		}

		return this.renderer;
	}

	private getSceneGeometry(scene: CanvasImageScene): SceneGeometry {
		const host = this.getSceneHost(scene);
		const hostRect = host.getBoundingClientRect();
		const initialRect = scene.initialContainer.getBoundingClientRect();
		const finalRect = scene.containerAfterTransfer.getBoundingClientRect();

		return {
			finalCenterX: finalRect.left - hostRect.left + finalRect.width / 2,
			initialCenterX: initialRect.left - hostRect.left + initialRect.width / 2,
			initialCenterY: initialRect.top - hostRect.top + initialRect.height / 2,
			initialHeight: initialRect.height,
			initialWidth: initialRect.width,
			stageHeight: host.clientHeight,
			stageWidth: host.clientWidth
		};
	}

	private getSceneHost(scene: CanvasImageScene) {
		const host = scene.initialContainer.parentElement;

		if (!host) {
			throw new Error('Initial container must have a parent element');
		}

		return host;
	}

	private getOrCreateTransferData({
		asset,
		finalCenterX,
		imageHeight,
		imageWidth,
		initialCenterX,
		initialCenterY
	}: CanvasImageTransferCacheParams) {
		const nextTransferSignature = [
			finalCenterX,
			imageHeight,
			imageWidth,
			initialCenterX,
			initialCenterY,
			this.currentSourceKey
		].join(':');

		if (
			!this.transferData ||
			this.transferSignature !== nextTransferSignature
		) {
			this.transferData = createCanvasImageTransferData({
				centerY: initialCenterY,
				endCenterX: finalCenterX,
				image: asset.image,
				imageHeight,
				imageWidth,
				sourceRect: asset.sourceRect,
				startCenterX: initialCenterX
			});
			this.transferSignature = nextTransferSignature;
		}

		return this.transferData;
	}

	private loadSource(source: CanvasImageSource) {
		if (typeof source !== 'string') {
			if (source.complete) {
				return Promise.resolve(source);
			}

			return new Promise<HTMLImageElement>((resolve, reject) => {
				source.onload = () => resolve(source);
				source.onerror = () =>
					reject(new Error('Failed to load image element'));
			});
		}

		return new Promise<HTMLImageElement>((resolve, reject) => {
			const image = new Image();
			image.crossOrigin = 'anonymous';
			image.onload = () => resolve(image);
			image.onerror = () =>
				reject(new Error(`Failed to load image: ${source}`));
			image.src = source;
		});
	}

	private requireScene() {
		if (!this.scene) {
			throw new Error('Canvas image scene is not configured');
		}

		return this.scene;
	}

	private smoothstep(edge0: number, edge1: number, value: number) {
		const t = this.clamp((value - edge0) / (edge1 - edge0 || 1));
		return t * t * (3 - 2 * t);
	}

	private stopAnimation() {
		if (this.animationFrameId !== null) {
			window.cancelAnimationFrame(this.animationFrameId);
			this.animationFrameId = null;
		}
	}
}

export const canvasImageEffectService = new CanvasImageEffectService();
